/**
 * ---------------------------------------------------------------------------
 * CART & MINI-CART INTERACTION TRACKING
 * ---------------------------------------------------------------------------
 *
 * Instruments every clickable thing on both cart surfaces so GA4 can answer
 * "where in the cart do people stop, and what do they touch on the way out".
 *
 * This is BASELINE INSTRUMENTATION, not an experiment. It changes nothing the
 * shopper sees. It is deliberately additive: it introduces its own `ss_cart_*`
 * event names and never emits the GA4 standard ecommerce names
 * (`view_cart`, `add_to_cart`, `remove_from_cart`, `begin_checkout`), because
 * those are already sent by Shopify's own Google channel pixel and by the GTM
 * container. Emitting them again here would corrupt existing ecommerce reports.
 *
 *
 * THE TWO SURFACES
 * ----------------
 * The store runs `cart_type: "drawer"` (config/settings_data.json:51), so there
 * are two carts and both are tracked, keyed by `cart_surface`:
 *
 *   drawer     <cart-drawer id="cart-drawer">   sections/cart-drawer.liquid
 *   cart_page  /cart                            sections/main-cart.liquid
 *
 * On /cart BOTH exist in the DOM at once, because the header still renders the
 * drawer. They are tracked independently. Always split cart reporting by
 * `cart_surface` or the two double-count as one.
 *
 *
 * TRANSPORT - READ THIS BEFORE EDITING
 * ------------------------------------
 * Two channels, and the ORDER OF IMPORTANCE is the opposite of what the
 * Exp-8/Exp-9 Convert scripts assume.
 *
 *   1. gtag, direct to GA4. THE PRIMARY PATH. Requires CONFIG.measurementId to
 *      be set; nothing reaches GA4 without it.
 *   2. dataLayer.push. Secondary, and currently inert.
 *
 * Why round that way. The GTM container on this storefront is GTM-P7KQTLKR,
 * which belongs to Max Connect Marketing, and **we do not have edit access to
 * it** (confirmed 2026-08-31). A dataLayer push is only a GA4 event if a tag in
 * that container picks it up, and we cannot add one. So relying on the dataLayer
 * the way the Convert scripts do would mean these events go nowhere.
 *
 * Sending direct means one extra `gtag('config', ...)` on the page alongside
 * whatever the container is doing. That is safe, and deliberately so:
 *
 *   - `send_page_view: false` on our config, so we never duplicate a page_view.
 *   - `send_to` on every event, so events go ONLY to the GA4 stream and are not
 *     also delivered to the AW-976948441 Google Ads destination that
 *     layout/theme.liquid configures.
 *   - Our event names are all `ss_cart_*`, which nothing else emits.
 *
 * The dataLayer push is kept anyway. It costs one line, it makes the events
 * visible in GTM Preview and in the browser dataLayer for debugging, and if Max
 * Connect ever adds an `^ss_` trigger the events are already there. If that
 * happens, set CONFIG.measurementId back to '' or the events will be counted
 * twice.
 *
 *
 * WHY DELEGATION, AND WHY composedPath()
 * --------------------------------------
 * Two hard constraints from the theme and the apps on top of it:
 *
 *   1. The drawer REPLACES ITS OWN CHILDREN on every cart change
 *      (assets/theme.js:1798-1837). Any listener bound to a button inside it
 *      dies the first time a quantity changes. So everything here is delegated
 *      from `document` with capture: true.
 *
 *   2. SavedBy Package Protection renders <savedby-checkout-plus> into
 *      `.route-div` as a SHADOW DOM component (its app-block settings use
 *      `:host` selectors, config/settings_data.json:222). A normal
 *      `event.target` is retargeted to the shadow host, so the toggle, the info
 *      "?" and the "Continue without protection" link would all read as one
 *      undifferentiated click. `event.composedPath()` sees through it.
 *
 * Capture phase also matters for correctness, not just robustness: on the CART
 * PAGE both Remove and quantity change do a full page navigation to
 * /cart/change (assets/theme.js:2024). The event has to be pushed before that
 * handler runs, and the GA4 tag has to go out on sendBeacon to survive unload.
 *
 *
 * THE LONG-TAIL CLICK
 * -------------------
 * `ss_cart_click` fires on EVERY click inside a cart surface, not only on the
 * elements listed in ACTIONS. Anything unrecognised is reported as
 * `cart_action: "other"` with a `cart_element` descriptor. That is the point:
 * the cart hosts third-party widgets (SavedBy, Route, Bogos, Rebuy, Videowise)
 * that can change without notice, and a fixed selector list would silently stop
 * seeing them. The long tail in `cart_element` is the inventory of what
 * shoppers actually touch.
 *
 *
 * PRIVACY
 * -------
 * No field VALUES are ever captured. `describeElement` reads text content from
 * buttons and links only, and explicitly refuses to read from inputs and
 * textareas, so the order note and the shipping-estimator postcode never leave
 * the page. Quantities are numbers, not identity.
 *
 *
 * Written 2026-08-31. Source of truth is this repo file.
 */
(function () {
    'use strict';

    /* ---------------------------------------------------------------- config */

    var CONFIG = {
        /* GA4 measurement ID for the singularsound.com web data stream.
         *
         * THE ONE SETTING THAT DECIDES WHETHER ANY OF THIS REACHES GA4. Set to
         * '' and the file falls back to dataLayer-only, which on this storefront
         * means the events go nowhere - see the TRANSPORT note above. */
        measurementId: 'G-P1B5YHZ105',

        /* Seconds a surface can sit visible with no interaction before it counts
         * as hesitation. 20s matches the Exp-9 spec so the two are comparable. */
        hesitationSeconds: 20,

        /* Fires at most once per surface open, so a shopper who sits for five
         * minutes produces one hesitation event, not fifteen. */
        hesitationOncePerOpen: true,

        /* Append ?ss_track_debug=1 to any URL to log every event to the console
         * without changing what is sent. Persists for the tab. */
        debug: false
    };

    try {
        if (/[?&]ss_track_debug=1/.test(window.location.search)) {
            window.sessionStorage.setItem('ss_track_debug', '1');
        }
        CONFIG.debug = window.sessionStorage.getItem('ss_track_debug') === '1';
    } catch (e) {}

    /* Namespaced under window.SS like the Convert global JS, so nothing here can
     * collide with the theme, SavedBy, PageFly or the other apps. */
    var SS = window.SS = window.SS || {};
    if (SS.cartTracking) return;            /* already running, do not double-bind */

    /* ------------------------------------------------------------- transport */

    /* Lives on the shared SS namespace rather than in this closure, because
     * assets/pdp-tracking.js configures the same stream and on a product page
     * both files are running. Whichever loads first configures it; the other
     * reuses it, so the property never sees two configs for one ID. */

    /* Register our GA4 stream as an additional gtag destination, once.
     *
     * layout/theme.liquid already defines window.gtag and loads gtag.js in
     * <head> for the AW-976948441 Google Ads destination, and this file is a
     * deferred script, so by the time it runs `gtag` exists and is queueing.
     * Adding a second destination to an existing gtag is supported and is the
     * documented way to send to two properties from one page.
     *
     * The script-injection branch is a safety net for the day someone removes
     * that Ads snippet from the layout. If gtag is missing entirely we bring our
     * own rather than silently stop collecting.
     *
     * send_page_view: false is not optional. Page views for this stream already
     * arrive from whatever the Max Connect container is doing; a second source
     * would inflate sessions and page views across the whole property, not just
     * the cart.
     */
    function ensureGa4() {
        if (SS.ga4Ready) return true;
        if (!/^G-[A-Z0-9]+$/i.test(CONFIG.measurementId)) return false;

        try {
            if (typeof window.gtag !== 'function') {
                window.dataLayer = window.dataLayer || [];
                window.gtag = function () { window.dataLayer.push(arguments); };

                var script = document.createElement('script');
                script.async = true;
                script.src = 'https://www.googletagmanager.com/gtag/js?id=' + CONFIG.measurementId;
                document.head.appendChild(script);

                window.gtag('js', new Date());
            }

            window.gtag('config', CONFIG.measurementId, { send_page_view: false });
            SS.ga4Ready = true;
        } catch (e) {}

        return !!SS.ga4Ready;
    }

    function trackEvent(name, params) {
        var payload = basePayload();

        for (var key in params) {
            if (Object.prototype.hasOwnProperty.call(params, key) && params[key] !== undefined && params[key] !== null) {
                payload[key] = params[key];
            }
        }

        /* Secondary, and inert until someone adds an ^ss_ trigger to the GTM
         * container. Kept for GTM Preview and console debugging. */
        try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(Object.assign({ event: name }, payload));
        } catch (e) {}

        /* Primary. */
        try {
            if (ensureGa4()) {
                window.gtag('event', name, Object.assign({
                    /* Restricts delivery to the GA4 stream. Without this the
                     * event also lands on the Google Ads destination that
                     * theme.liquid configures, which nobody wants. */
                    send_to: CONFIG.measurementId,
                    /* Beacon so the hit survives the /cart/change navigation
                     * that Remove and quantity change trigger on /cart. */
                    transport_type: 'beacon'
                }, payload));
            }
        } catch (e) {}

        if (CONFIG.debug) {
            try {
                console.log('%c[ss-cart-track]', 'color:#f0c417', name,
                    SS.ga4Ready ? '-> ' + CONFIG.measurementId : '-> dataLayer only', payload);
            } catch (e) {}
        }
    }

    function basePayload() {
        return {
            page_type: pageType(),
            page_path: location.pathname,
            viewport_width: window.innerWidth,
            viewport_group: window.innerWidth < 700 ? 'mobile' : (window.innerWidth < 1000 ? 'tablet' : 'desktop')
        };
    }

    function pageType() {
        try {
            if (window.themeVariables && window.themeVariables.settings) {
                return window.themeVariables.settings.pageType || 'unknown';
            }
        } catch (e) {}
        return location.pathname.indexOf('/cart') === 0 ? 'cart' : 'unknown';
    }

    /* ------------------------------------------------------------ cart state */

    /* Last known /cart.js payload. Seeded lazily and then kept current from the
     * theme's own `cart:change` event (assets/theme.js:2050, :2832), so no extra
     * network request happens on a normal browse. */
    var cart = null;
    var cartFetchInFlight = false;

    document.addEventListener('cart:change', function (event) {
        try {
            if (event.detail && event.detail.cart) cart = event.detail.cart;
        } catch (e) {}
    });

    function refreshCart() {
        if (cartFetchInFlight) return;
        cartFetchInFlight = true;

        try {
            var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            fetch(root + 'cart.js', { credentials: 'same-origin' })
                .then(function (r) { return r.json(); })
                .then(function (json) { cart = json; })
                .catch(function () {})
                .then(function () { cartFetchInFlight = false; });
        } catch (e) {
            cartFetchInFlight = false;
        }
    }

    function cartContext() {
        if (!cart) return {};

        return {
            cart_value: typeof cart.total_price === 'number' ? cart.total_price / 100 : undefined,
            cart_item_count: cart.item_count,
            cart_line_count: cart.items ? cart.items.length : undefined,
            cart_currency: cart.currency
        };
    }

    /* Resolve a line item from anywhere inside its row.
     *
     * Both surfaces wrap the quantity input and the Remove link in the same
     * <line-item-quantity> element carrying a [data-line-key] input, so that is
     * the reliable anchor. The product title link sits outside it, in
     * <line-item>, so both are tried. On /cart the quantity cell is a sibling
     * <td> of the line-item cell (sections/main-cart.liquid:54), which is why a
     * <tr> fallback exists. */
    function lineItemContext(el) {
        if (!el || !el.closest) return {};

        var scope = el.closest('line-item-quantity') || el.closest('line-item') || el.closest('tr');
        if (!scope) return {};

        var input = scope.querySelector('[data-line-key]');
        var key = input && input.getAttribute('data-line-key');

        /* Remove links carry the key in the href instead: /cart/change?id=KEY&quantity=0 */
        if (!key) {
            var link = scope.querySelector('a[href*="/cart/change"]');
            if (link) {
                try { key = new URL(link.href, location.origin).searchParams.get('id'); } catch (e) {}
            }
        }

        if (!key) return {};

        var context = { line_key: String(key).slice(0, 100) };

        if (cart && cart.items) {
            for (var i = 0; i < cart.items.length; i++) {
                if (cart.items[i].key === key) {
                    context.item_title = String(cart.items[i].product_title || cart.items[i].title || '').slice(0, 100);
                    context.item_variant_id = cart.items[i].variant_id;
                    context.item_quantity = cart.items[i].quantity;
                    context.item_price = typeof cart.items[i].final_price === 'number' ? cart.items[i].final_price / 100 : undefined;
                    break;
                }
            }
        }

        return context;
    }

    /* ---------------------------------------------------------- surface model */

    /* One record per surface, holding the state the exit and hesitation events
     * are computed from. Reset on every open. */
    function newSurfaceState() {
        return {
            open: false,
            openedAt: 0,
            actionCount: 0,
            lastAction: null,
            lastActionAt: 0,
            reachedCheckout: false,
            /* Sticky, and deliberately never overwritten once set. `lastAction`
             * is whatever happened most recently, which is the wrong field for
             * "which route to checkout did they take": a vendor widget that
             * re-renders the cart after an opt-out can land another action on
             * top of it. These two survive that. */
            checkoutPath: null,
            checkoutVendor: null,
            hesitationFired: false,
            hesitationTimer: null,
            exitTo: null,
            exitFired: false
        };
    }

    var surfaces = {
        drawer: newSurfaceState(),
        cart_page: newSurfaceState()
    };

    /* Which surface did this event happen INSIDE?
     *
     * Strict: returns null when the event did not originate within a cart
     * container. There is deliberately no "we are on /cart, so call it the cart
     * page" fallback here, because on /cart that fallback would claim every
     * click on the page - header, nav, footer, newsletter - and bury the actual
     * cart signal under `cart_action: other`. The two callers that legitimately
     * need a fallback apply their own.
     *
     * Walks the composed path rather than reading event.target, so a click
     * inside SavedBy's shadow DOM is still attributed to the surface it is
     * rendered into. Drawer is tested first at every level, because on /cart the
     * drawer is nested in the same document as the page cart. */
    function surfaceIn(path) {
        for (var i = 0; i < path.length; i++) {
            var node = path[i];
            if (!node || !node.tagName) continue;

            if (node.tagName.toLowerCase() === 'cart-drawer') return 'drawer';

            if (node.classList) {
                if (node.classList.contains('cart-drawer')) return 'drawer';

                if (node.classList.contains('cart') ||
                    node.classList.contains('cart-order') ||
                    node.classList.contains('cart-form')) return 'cart_page';

                /* An empty /cart renders only `.empty-state` - no `.cart`
                 * wrapper - so "Continue shopping" would otherwise be
                 * unattributable. The drawer's own empty state is already
                 * caught above by the cart-drawer ancestor. */
                if (node.classList.contains('empty-state') && pageType() === 'cart') return 'cart_page';
            }
        }

        return null;
    }

    function surfacePayload(surfaceKey) {
        var state = surfaces[surfaceKey];

        return Object.assign({
            cart_surface: surfaceKey,
            dwell_seconds: state && state.openedAt ? Math.round((Date.now() - state.openedAt) / 1000) : undefined,
            action_count: state ? state.actionCount : undefined
        }, cartContext());
    }

    /* -------------------------------------------------------------- open/exit */

    function openSurface(surfaceKey, source) {
        var state = surfaces[surfaceKey];
        if (!state || state.open) return;

        surfaces[surfaceKey] = state = newSurfaceState();
        state.open = true;
        state.openedAt = Date.now();
        state.lastActionAt = Date.now();

        refreshCart();

        /* Cart context is fetched async on a cold open, so give it a beat before
         * the view event goes out. 250ms is well under the time it takes anyone
         * to read the drawer, and the event still fires if the fetch fails. */
        window.setTimeout(function () {
            trackEvent('ss_cart_view', Object.assign(surfacePayload(surfaceKey), {
                cart_open_source: source || 'unknown'
            }));
        }, 250);

        scheduleHesitation(surfaceKey);
    }

    function closeSurface(surfaceKey, reason) {
        var state = surfaces[surfaceKey];
        if (!state || !state.open || state.exitFired) return;

        state.open = false;
        state.exitFired = true;
        clearHesitation(surfaceKey);

        trackEvent('ss_cart_exit', Object.assign(surfacePayload(surfaceKey), {
            exit_reason: reason,
            exit_to: state.exitTo || (reason === 'drawer_closed' ? 'stayed_on_page' : 'unknown'),
            reached_checkout: state.reachedCheckout ? 'true' : 'false',
            last_action: state.lastAction || 'none',
            /* Only present when a route to checkout was taken. This is what
             * makes "opted out, then left" separable from "kept protection,
             * then left" without needing a sequence segment. */
            checkout_path: state.checkoutPath || undefined,
            protection_vendor: state.checkoutVendor || undefined
        }));
    }

    /* Where did they go? Classified rather than raw URL, because a raw URL is a
     * high-cardinality dimension GA4 will not aggregate usefully. GA4's own Path
     * Exploration still has the exact next page for anyone who needs it. */
    function classifyDestination(href) {
        if (!href) return 'unknown';

        var url;
        try { url = new URL(href, location.origin); } catch (e) { return 'unknown'; }

        if (url.origin !== location.origin) return 'external';

        var p = url.pathname;
        if (p.indexOf('/checkouts') === 0 || p.indexOf('/checkout') === 0) return 'checkout';
        if (p.indexOf('/products/') === 0) return 'product';
        if (p.indexOf('/collections/') === 0) return 'collection';
        if (p.indexOf('/policies/') === 0) return 'policy';
        if (p.indexOf('/pages/') === 0) return 'page';
        if (p.indexOf('/blogs/') === 0) return 'blog';
        if (p.indexOf('/account') === 0) return 'account';
        if (p.indexOf('/search') === 0) return 'search';
        if (p.indexOf('/cart') === 0) return 'cart';
        if (p === '/' || p === '') return 'home';
        return 'other';
    }

    /* ------------------------------------------------------------ hesitation */

    function scheduleHesitation(surfaceKey) {
        var state = surfaces[surfaceKey];
        if (!state) return;

        clearHesitation(surfaceKey);

        if (CONFIG.hesitationOncePerOpen && state.hesitationFired) return;

        state.hesitationTimer = window.setTimeout(function () {
            if (!state.open) return;
            state.hesitationFired = true;

            trackEvent('ss_cart_hesitation', Object.assign(surfacePayload(surfaceKey), {
                last_action: state.lastAction || 'none',
                idle_seconds: CONFIG.hesitationSeconds
            }));
        }, CONFIG.hesitationSeconds * 1000);
    }

    function clearHesitation(surfaceKey) {
        var state = surfaces[surfaceKey];
        if (state && state.hesitationTimer) {
            window.clearTimeout(state.hesitationTimer);
            state.hesitationTimer = null;
        }
    }

    function noteAction(surfaceKey, action) {
        var state = surfaces[surfaceKey];
        if (!state) return;

        state.actionCount += 1;
        state.lastAction = action;
        state.lastActionAt = Date.now();

        scheduleHesitation(surfaceKey);
    }

    /* -------------------------------------------------- element description */

    /* A compact descriptor of what was clicked.
     *
     * Deliberately does NOT read value or text from form fields, so the order
     * note and the postcode in the shipping estimator can never be sent. Text is
     * capped at 40 characters and the whole string at 90, which keeps it under
     * GA4's 100-character parameter-value limit with room to spare. */
    function describeElement(el) {
        if (!el || !el.tagName) return 'unknown';

        var tag = el.tagName.toLowerCase();
        var parts = [tag];

        var label = el.getAttribute && (el.getAttribute('aria-label') || el.getAttribute('name') || el.getAttribute('data-testid'));
        if (label) parts.push('@' + String(label).slice(0, 30));

        var cls = '';
        if (typeof el.className === 'string') cls = el.className;
        else if (el.className && el.className.baseVal) cls = el.className.baseVal;   /* SVG */

        cls = cls.trim().split(/\s+/).filter(Boolean).slice(0, 2).join('.');
        if (cls) parts.push('.' + cls.slice(0, 40));

        var readsText = tag !== 'input' && tag !== 'textarea' && tag !== 'select' &&
            !(el.closest && el.closest('input, textarea, select'));

        if (readsText) {
            var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
            if (text) parts.push('"' + text.slice(0, 40) + '"');
        }

        return parts.join(' ').slice(0, 90);
    }

    /* The nearest thing in the composed path that a human would call "the thing
     * I clicked". Falls back to the raw target so nothing is dropped. */
    function actionableIn(path) {
        var interactive = 'a,button,summary,label,input,select,textarea,[role="button"],[role="switch"],[role="checkbox"],[aria-controls]';

        for (var i = 0; i < path.length; i++) {
            var node = path[i];
            if (!node || !node.tagName || !node.matches) continue;
            try { if (node.matches(interactive)) return node; } catch (e) {}
        }

        return path[0] && path[0].tagName ? path[0] : null;
    }

    /* --------------------------------------------------------- classification */

    /* Ordered. First match wins, so the specific rules sit above the generic
     * ones. Each test receives the composed path and the actionable element.
     *
     * The protection widget deserves a note: it hides the theme's own checkout
     * button and renders its own in its place (see the Exp-9 control script),
     * so matching only `button[name="checkout"]` would under-count checkout
     * clicks. Both are matched, and `checkout_path` records which was used.
     *
     * ORDERING TRAP: `protection_optout` MUST sit above `checkout`. ShipInsure's
     * opt-out reads "or checkout without protection" and SavedBy's read
     * "Continue without package protection". The generic checkout rule below
     * matches any interactive element whose text STARTS WITH "checkout", so any
     * vendor that drops the leading "or" would have its opt-out counted as a
     * standard checkout - silently inverting the one number this widget exists
     * to answer. Specific beats generic; do not reorder these. */
    var ACTIONS = [
        {
            action: 'protection_optout',
            test: function (path, el) {
                if (!inProtectionWidget(path)) return false;
                /* Covers SavedBy's "Continue without package protection" and
                 * ShipInsure's "or checkout without protection". */
                return /without\s+(package\s+|shipping\s+|order\s+)?protection|continue\s+without|without\s+package|without\s+insurance|no\s+thanks|decline/i.test(textOf(el));
            }
        },
        {
            action: 'checkout',
            test: function (path, el) {
                if (matchesAny(path, 'button[name="checkout"],[name="checkout"]')) return true;
                return isInteractive(el) && /^checkout/i.test(textOf(el));
            }
        },
        {
            action: 'checkout_accelerated',
            test: function (path) {
                return matchesAny(path, '.additional-checkout-buttons,.additional-checkout-buttons--vertical,shopify-payment-button,[data-shopify="payment-button"]');
            }
        },
        {
            action: 'protection_info',
            test: function (path, el) {
                if (!inProtectionWidget(path)) return false;

                /* The actual modal trigger. ShipInsure's is a bare span with
                 * role="button", no class, so id and aria-label are the only
                 * durable handles. ⓘ and ℹ are the circled-i glyphs
                 * used as the icon in place of an svg. */
                if (matchesAny(path, '[id*="info-modal" i],[aria-label*="information" i],[class*="tooltip" i],svg,img')) return true;
                if (/^[?iⓘℹ]$/.test(textOf(el))) return true;

                /* Looser class match for other vendors, minus the parts of the
                 * row that merely happen to sit in an "info" block. Without this
                 * guard, ShipInsure's `_si-info-widget-header` and
                 * `_si-info-widget-price` would both read as info clicks. */
                var cls = typeof el.className === 'string' ? el.className : '';
                return matchesAny(path, '[class*="info" i]') && !/header|title|label|price/i.test(cls);
            }
        },
        {
            action: 'protection_toggle',
            test: function (path) {
                return inProtectionWidget(path) && matchesAny(path, 'input[type="checkbox"],[role="switch"],[aria-checked],[class*="toggle" i],[class*="switch" i]');
            }
        },
        {
            action: 'protection_other',
            test: function (path) { return inProtectionWidget(path); }
        },
        {
            action: 'route_widget',
            test: function (path) {
                return matchesAny(path, '.route-div,[data-route-copy],[class*="route" i]');
            }
        },
        {
            action: 'remove_item',
            test: function (path) {
                return matchesAny(path, 'a[href*="/cart/change"]');
            }
        },
        {
            action: 'quantity_field_focus',
            test: function (path) {
                return matchesAny(path, 'input[data-line-key]');
            }
        },
        {
            action: 'quantity_stepper',
            test: function (path) {
                return matchesAny(path, '.quantity-selector__button,[data-ss-quantity-step]');
            }
        },
        {
            action: 'view_cart',
            test: function (path, el) {
                if (surfaceIn(path) !== 'drawer') return false;
                var href = el && el.getAttribute && el.getAttribute('href');
                return !!href && /\/cart(\?|$)/.test(href);
            }
        },
        {
            action: 'continue_shopping',
            test: function (path, el) {
                if (el && el.closest && el.closest('.empty-state')) return true;
                return /continue\s+shopping/i.test(textOf(el));
            }
        },
        {
            action: 'note_open',
            test: function (path) {
                return matchesAny(path, '[aria-controls="cart-drawer-note"]');
            }
        },
        {
            action: 'note_field_focus',
            test: function (path) {
                return matchesAny(path, 'textarea[name="note"]');
            }
        },
        {
            action: 'note_save',
            test: function (path, el) {
                return !!(el && el.closest && el.closest('cart-note, cart-note-dialog')) && isInteractive(el);
            }
        },
        {
            action: 'shipping_estimator_submit',
            test: function (path) {
                return matchesAny(path, 'shipping-estimator [type="submit"]');
            }
        },
        {
            action: 'shipping_estimator_field',
            test: function (path) {
                return matchesAny(path, 'shipping-estimator input,shipping-estimator select');
            }
        },
        {
            action: 'shipping_estimator_toggle',
            test: function (path) {
                return matchesAny(path, 'details.accordion summary,.accordion summary');
            }
        },
        {
            action: 'policy_link',
            test: function (path, el) {
                var href = el && el.getAttribute && el.getAttribute('href');
                return !!href && href.indexOf('/policies/') !== -1;
            }
        },
        {
            action: 'recommendation_add',
            test: function (path, el) {
                if (!el || !el.closest || !el.closest('.horizontal-product')) return false;
                return el.type === 'submit' || /add/i.test(textOf(el));
            }
        },
        {
            action: 'recommendation_carousel',
            test: function (path) {
                return matchesAny(path, '[is="prev-button"],[is="next-button"]');
            }
        },
        {
            action: 'recommendation_click',
            test: function (path, el) {
                return !!(el && el.closest && el.closest('.horizontal-product,.cart-drawer__recommendations'));
            }
        },
        {
            action: 'product_link',
            test: function (path, el) {
                var href = el && el.getAttribute && el.getAttribute('href');
                return !!href && href.indexOf('/products/') !== -1;
            }
        },
        {
            action: 'close_drawer',
            test: function (path) {
                return matchesAny(path, '[is="close-button"],.drawer__close-icon');
            }
        },
        {
            action: 'free_shipping_bar',
            test: function (path) { return matchesAny(path, 'free-shipping-bar'); }
        },
        {
            action: 'discount_badge',
            test: function (path) { return matchesAny(path, '.badge'); }
        }
    ];

    function classify(path, el) {
        for (var i = 0; i < ACTIONS.length; i++) {
            try {
                if (ACTIONS[i].test(path, el)) return ACTIONS[i].action;
            } catch (e) {}
        }
        return 'other';
    }

    function matchesAny(path, selector) {
        for (var i = 0; i < path.length; i++) {
            var node = path[i];
            if (!node || !node.matches) continue;
            try { if (node.matches(selector)) return true; } catch (e) {}
        }
        return false;
    }

    /* The protection widget is deliberately matched by VENDOR TOKEN, not by one
     * vendor's markup. The store ran SavedBy until 2026-09-09 and ShipInsure
     * after it (config/settings_data.json, the SavedBy block is disabled), and
     * the next swap will happen without telling us either. Anything that looks
     * like a protection upsell is caught here; `protection_vendor` records which
     * one it was, so a before/after read is a dimension split rather than a
     * rewrite of this file. */
    var VENDORS = [
        { name: 'savedby', tag: 'savedby', pattern: /savedby|sb__/i },
        /* `_si-` is ShipInsure's real prefix, confirmed against the live drawer
         * on 2026-09-14: `_si-widget-container`, `_si-info-widget-header`,
         * `_si-info-modal`, `_si-sec-checkout-id`. Nothing it renders contains
         * the string "shipinsure", so the name alone would never have matched. */
        { name: 'shipinsure', tag: 'shipinsure', pattern: /_si-|shipinsure|ship-insure|ship_insure/i }
    ];

    /* Vendor-neutral fallback. Only consulted after the named vendors miss, so a
     * known widget is never mislabelled as `unknown`. Kept narrow on purpose:
     * "protection" and "insurance" next to a cart control is the upsell, but
     * "protection" alone appears in policy links, which are matched earlier. */
    var PROTECTION_HINT = /package-protection|shipping-protection|order-protection|package_protection|protection-widget|insurance/i;

    /* Returns the vendor name, '' when the path is not in a protection widget. */
    function protectionVendor(path) {
        var fallback = '';

        for (var i = 0; i < path.length; i++) {
            var node = path[i];
            if (!node || !node.tagName) continue;

            var tag = node.tagName.toLowerCase();
            var idAndClass = (node.id || '') + ' ' + (typeof node.className === 'string' ? node.className : '');

            for (var v = 0; v < VENDORS.length; v++) {
                if (tag.indexOf(VENDORS[v].tag) === 0) return VENDORS[v].name;
                if (VENDORS[v].pattern.test(idAndClass)) return VENDORS[v].name;
            }

            /* Remember, but keep looking: a named vendor further up the path
             * always wins over the generic hint. */
            if (!fallback && (PROTECTION_HINT.test(idAndClass) || PROTECTION_HINT.test(tag))) {
                fallback = 'unknown';
            }
        }

        return fallback;
    }

    function inProtectionWidget(path) {
        return protectionVendor(path) !== '';
    }

    function textOf(el) {
        if (!el || !el.tagName) return '';

        var tag = el.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return '';

        return (el.textContent || '').replace(/\s+/g, ' ').trim();
    }

    function isInteractive(el) {
        if (!el || !el.matches) return false;
        try { return el.matches('a,button,summary,[role="button"]'); } catch (e) { return false; }
    }

    /* ----------------------------------------------------------- click stream */

    document.addEventListener('click', function (event) {
        var path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
        var el = actionableIn(path);

        /* STEP 1 - destination capture, for any link clicked while a surface is
         * open, INCLUDING links outside the cart.
         *
         * This is how ss_cart_exit knows where they went. A shopper who gives up
         * on /cart and taps a nav link or a footer link is exactly the case
         * worth catching, and that click is not inside the cart. It records the
         * destination without firing a cart click event, so the interaction
         * inventory stays clean. */
        var href = el && el.getAttribute && el.getAttribute('href');
        var destination = null;

        if (href && href.charAt(0) !== '#' && href.indexOf('/cart/change') === -1) {
            destination = classifyDestination(href);

            for (var key in surfaces) {
                if (surfaces[key].open) surfaces[key].exitTo = destination;
            }
        }

        /* STEP 2 - the in-cart click stream. Strict: a click outside a cart
         * container is not a cart interaction and stops here. */
        var surfaceKey = surfaceIn(path);
        if (!surfaceKey) return;

        /* The cart page seeds its surface on load, but seed here too in case a
         * click beat the load handler or the drawer opened without announcing
         * itself. */
        if (!surfaces[surfaceKey].open) openSurface(surfaceKey, 'implicit');

        var action = classify(path, el);

        noteAction(surfaceKey, action);

        var vendor = protectionVendor(path);

        var payload = Object.assign(surfacePayload(surfaceKey), lineItemContext(el), {
            cart_action: action,
            cart_element: describeElement(el),
            click_destination: destination || undefined,
            /* Only set inside the protection widget, so the dimension stays
             * clean: "not set" means "this click was not the upsell". */
            protection_vendor: vendor || undefined
        });

        trackEvent('ss_cart_click', payload);

        /* Checkout gets its own event as well. It is the funnel step that joins
         * this file to the checkout pixel, and keeping it separate means the
         * funnel does not have to filter on a parameter to find its step. */
        if (action === 'checkout' || action === 'checkout_accelerated' || action === 'protection_optout') {
            var checkoutPath = action === 'checkout_accelerated' ? 'accelerated_checkout'
                : action === 'protection_optout' ? 'without_protection'
                    : 'standard';

            surfaces[surfaceKey].reachedCheckout = true;
            surfaces[surfaceKey].exitTo = 'checkout';

            /* First route taken wins. Someone who opts out and then clicks the
             * theme's own checkout button is still an opt-out, and recording
             * them as `standard` would quietly shrink the opt-out rate. */
            if (!surfaces[surfaceKey].checkoutPath) {
                surfaces[surfaceKey].checkoutPath = checkoutPath;
                surfaces[surfaceKey].checkoutVendor = vendor || 'none';
            }

            trackEvent('ss_cart_checkout_click', Object.assign(surfacePayload(surfaceKey), {
                checkout_path: checkoutPath,
                cart_element: describeElement(el),
                protection_vendor: vendor || undefined
            }));
        }

        if (action === 'close_drawer') {
            /* Let the drawer's own hide transition run, then report the exit. */
            window.setTimeout(function () { closeSurface('drawer', 'drawer_closed'); }, 50);
        }
    }, true);

    /* ------------------------------------------------------- quantity changes */

    /* Stash the pre-edit value so the change event can report a direction.
     * focusin rather than focus, because focus does not bubble. */
    document.addEventListener('focusin', function (event) {
        var el = event.target;
        if (el && el.hasAttribute && el.hasAttribute('data-line-key')) {
            el.setAttribute('data-ss-prev', el.value);
        }
    }, true);

    document.addEventListener('change', function (event) {
        var el = event.target;
        if (!el || !el.hasAttribute || !el.hasAttribute('data-line-key')) return;

        var path = typeof event.composedPath === 'function' ? event.composedPath() : [el];
        /* Fallback is legitimate here, unlike on click: a quantity change is
         * unambiguously a cart interaction wherever it came from, including a
         * keyboard-only edit where the composed path may not reach a container. */
        var surfaceKey = surfaceIn(path) || (pageType() === 'cart' ? 'cart_page' : 'drawer');

        var from = parseInt(el.getAttribute('data-ss-prev'), 10);
        var to = parseInt(el.value, 10);

        if (isNaN(to)) return;
        if (!isNaN(from) && from === to) return;      /* focus then blur, no real change */

        var direction = isNaN(from) ? 'unknown' : (to === 0 ? 'remove' : (to > from ? 'increase' : 'decrease'));

        noteAction(surfaceKey, 'quantity_' + direction);

        trackEvent('ss_cart_qty_change', Object.assign(surfacePayload(surfaceKey), lineItemContext(el), {
            qty_from: isNaN(from) ? undefined : from,
            qty_to: to,
            qty_direction: direction,
            /* Only one method exists today: type into the field. When a +/-
             * stepper ships, its buttons set data-ss-method="stepper" on the
             * input and this parameter becomes the before/after comparison. */
            qty_method: el.getAttribute('data-ss-method') || 'typed'
        }));

        el.removeAttribute('data-ss-method');
    }, true);

    /* ------------------------------------------------------ surface lifecycle */

    /* The drawer is a DialogElement; it announces itself once the show/hide
     * transition has finished (assets/theme.js:1444, :1464). */
    document.addEventListener('dialog:after-show', function (event) {
        var el = event.target;
        if (el && el.id === 'cart-drawer') {
            openSurface('drawer', document.querySelector('[aria-controls="cart-drawer"]:focus') ? 'header_icon' : 'auto');
        }
    }, true);

    document.addEventListener('dialog:after-hide', function (event) {
        var el = event.target;
        if (el && el.id === 'cart-drawer') closeSurface('drawer', 'drawer_closed');
    }, true);

    /* A cart change while the drawer is open replaces its children, which is not
     * a close. Keep the surface alive and just restart the hesitation clock. */
    document.addEventListener('cart:change', function () {
        if (surfaces.drawer.open) scheduleHesitation('drawer');
    });

    function startCartPage() {
        if (pageType() !== 'cart') return;
        openSurface('cart_page', 'page_load');
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', startCartPage);
    } else {
        startCartPage();
    }

    /* pagehide rather than beforeunload: it fires on bfcache navigations and on
     * mobile tab switches, where beforeunload does not. */
    function closeOpenSurfaces(reason) {
        for (var key in surfaces) {
            if (surfaces[key].open) closeSurface(key, reason);
        }
    }

    window.addEventListener('pagehide', function () { closeOpenSurfaces('page_left'); });

    /* A second trigger, not a replacement. pagehide is the reliable one on a
     * normal navigation, but a third-party widget that tears the page down its
     * own way can beat it, and iOS Safari has historically dropped it on a
     * backgrounded tab. closeSurface is guarded by `exitFired`, so whichever
     * arrives first wins and the other is a no-op. This cannot double-count. */
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') closeOpenSurfaces('page_hidden');
    });

    SS.cartTracking = {
        version: '1.0.0',
        track: trackEvent,
        state: surfaces,
        config: CONFIG
    };
})();
